/**
 * Edit one aspect as JSON and file the edit as a DRAFT (band D) — a pending `replace_aspect`
 * action in agent_actions, written only when a super admin approves it in To decide.
 *
 * The edit starts from the value on screen and sends it back as `base`: the backend refuses an edit
 * made against a value that has since changed (409), and the approve checks again, so an approved
 * draft never overwrites research or another draft that landed in between.
 *
 * Nothing leaves the browser until the JSON parses to the column's shape (an array or an object,
 * as stored) and differs from what is stored — the textarea says which of those it is not.
 */
import React from 'react';
import { draftAspect } from '@/services/categoryCockpitActions';
import { Act, type ActionDef } from './Act';
import { Drafted } from './Chat';

/** `why` is set exactly when `ok` is false */
type Check = { ok: boolean; value?: unknown; why?: string };

/** does the text parse to a value of the stored value's shape, and does it change anything? */
export function checkEdit(text: string, base: unknown): Check {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (e) {
    return { ok: false, why: `not valid JSON — ${e instanceof Error ? e.message : String(e)}` };
  }
  const wantArray = Array.isArray(base);
  if (value === null || typeof value !== 'object' || Array.isArray(value) !== wantArray) {
    return { ok: false, why: `this aspect is stored as ${wantArray ? 'an array' : 'an object'}; the edit is ${value === null ? 'null' : Array.isArray(value) ? 'an array' : typeof value}` };
  }
  if (JSON.stringify(value) === JSON.stringify(base)) return { ok: false, why: 'unchanged — there is nothing to draft' };
  return { ok: true, value };
}

const entries = (v: unknown) => (Array.isArray(v) ? v.length : v && typeof v === 'object' ? Object.keys(v).length : 0);

export function AspectDraftEditor({ sectorId, path, aspect, aspectLabel, base, siteId }: {
  sectorId: number; path: string; aspect: string; aspectLabel: string; base: unknown; siteId: string | null;
}) {
  const initial = React.useMemo(() => JSON.stringify(base ?? null, null, 2), [base]);
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(initial);
  // a new stored value (after a refresh) restarts the edit from it
  React.useEffect(() => { setText(initial); }, [initial]);

  if (base === null || base === undefined || typeof base !== 'object') {
    return <div className="pmc-small pmc-mute">Nothing stored to edit — research the aspect first.</div>;
  }
  if (!open) {
    return <button type="button" className="pmc-btn" onClick={() => setOpen(true)}>Edit as JSON (draft)</button>;
  }
  const check = checkEdit(text, base);
  const def: ActionDef = {
    label: 'Propose this edit as a draft',
    op: `draft-aspect:${sectorId}:${path}:${aspect}`,
    disabled: !siteId ? 'Select a site in the header: a draft is filed under a site of this sector' : check.ok ? undefined : `Cannot draft: ${check.why}`,
    confirm: {
      title: `Draft a new ${aspectLabel.toLowerCase()} for this category`,
      verb: 'Draft',
      danger: true,
      ask: { label: 'Why this edit? (stored with the draft)', placeholder: 'e.g. remove the duplicated "Commuter" persona', min: 3 },
      writes: <>one <b>pending</b> <code>replace_aspect</code> row in <code>agent_actions</code>, filed under the selected site. <b>The category is not touched</b> until a super admin approves it in To decide.</>,
      warn: <>Approving it <b>replaces the whole <code>{aspect}</code> column</b> ({entries(base)} → {check.ok ? entries(check.value) : '?'} entries). The previous value is kept in <code>category_characteristics_research_history</code>, and the approve is refused if the column changed in the meantime.</>,
    },
    run: (note) => {
      if (!siteId || !check.ok) return Promise.reject(new Error(!siteId ? 'no site selected' : check.why));
      return draftAspect(sectorId, path, aspect, { siteId, data: check.value, base, note });
    },
    render: (r) => <Drafted body={r.body} />,
  };
  return (
    <div className="pmk-draft-editor">
      <label className="pmk-ask">
        <span>{aspectLabel} as stored — edit, then propose it as a draft</span>
        <textarea className="pmc-input pmk-json-edit" value={text} spellCheck={false} rows={14} onChange={(e) => setText(e.target.value)} aria-label={`${aspectLabel} JSON`} />
      </label>
      <div className={`pmc-small ${check.ok ? 'pmc-mute' : 'pmk-warn'}`} data-testid="draft-check">
        {check.ok ? `${entries(base)} → ${entries(check.value)} entries` : check.why}
      </div>
      <div className="pmk-actrow">
        <Act def={def} />
        <button type="button" className="pmc-btn" onClick={() => { setText(initial); setOpen(false); }}>Cancel the edit</button>
      </div>
    </div>
  );
}
